//*Libraries imports
import React, { useState } from "react";
import { Dialog, DialogTitle, DialogContent, IconButton } from "@mui/material";
import { Visibility as VisibilityIcon } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import { Check, SendIcon } from "lucide-react";

//*Hooks Imports
import { useSelector } from "react-redux";

//*Local Imports
import { RootState } from "@app/store";
import { DataTable } from "@components/ui";
import type { Column } from "@components/ui/DataTable";
import { LoadingSkeleton } from "@components/ui/LoadingSkeleton";
import { StatusLabels } from "@features/Client/types";
import { API_URL } from "@/config/api";
import { FilledFormOutputDTO } from "@features/filledForm";
import PhaseFormList from "@features/phaseForm/PhaseFormList";
import { IProduct, IProductPhase, IActivity } from "@features/Element/types";
import { DocumentOutputDTO } from "@features/document/types";

const getFirstFieldValue = (formData: any[]): string => {
  if (!formData || !Array.isArray(formData)) {
    return "N/A";
  }

  const findFirstPrimitiveValue = (obj: any): string | null => {
    if (obj === null || obj === undefined) {
      return null;
    }

    if (Array.isArray(obj)) {
      for (const item of obj) {
        const result = findFirstPrimitiveValue(item);
        if (result) return result;
      }
      return null;
    }

    if (typeof obj === "object") {
      // Prioridade para campos comuns de 'label' ou 'name' em objetos complexos
      if (obj.label && typeof obj.label !== "object") return String(obj.label);
      if (obj.name && typeof obj.name !== "object") return String(obj.name);
      if (obj.value && typeof obj.value !== "object") return String(obj.value);
      if (obj.text && typeof obj.text !== "object") return String(obj.text);

      const keys = Object.keys(obj);
      for (const key of keys) {
        const value = obj[key];
        if (value !== null && value !== undefined && value !== "") {
          if (typeof value !== "object") {
            return String(value);
          }
          const result = findFirstPrimitiveValue(value);
          if (result) return result;
        }
      }
      return null;
    }

    if (typeof obj !== "object" && obj !== null && obj !== "") {
      return String(obj);
    }

    return null;
  };

  // Tenta encontrar o primeiro campo que não seja apenas metadata ou IDs
  for (const item of formData) {
    if (
      item.field?.type !== "PAGE" &&
      item.field?.type !== "SECTION" &&
      item.data
    ) {
      const result = findFirstPrimitiveValue(item.data);
      if (result) {
        return result;
      }
    }
  }

  return "N/A";
};

interface ClientViewModalProps {
  selectedClient: any;
  setSelectedClient: (client: any) => void;
  products: IProduct[];
  documents: DocumentOutputDTO[];
  productsIds: Record<string, number>;
  loading?: boolean;
  error?: any;
}

const ClientViewModal: React.FC<ClientViewModalProps> = ({
  selectedClient,
  setSelectedClient,
  products,
  documents,
  productsIds,
  loading,
  error,
}) => {
  const [selectType, setSelectType] = useState("formulario");
  const [productsIdSelect, setProductsIdSelect] = useState<IProduct | null>(
    null,
  );
  const [selectedPhase, setSelectedPhase] = useState<IProductPhase | null>(
    null,
  );
  const [selectedActivity, setSelectedActivity] = useState<IActivity | null>(
    null,
  );
  const [selectTypeModal, setSelectTypeModal] = useState("fases");
  const [activitiesChecked, setActivitiesChecked] = useState<
    Record<string, any>
  >({});
  const [selectTypeModalDoc, setSelectTypeModalDoc] = useState("documentos");

  const { items: phaseForms } = useSelector(
    (state: RootState) => state.phaseForm,
  );

  const saveClientProduct = (e: any) => {
    fetch(API_URL + "/clients/save_client_product", {
      method: "POST",
      headers: {
        Authorization: localStorage.getItem("token") || "",
        Accept: "application/json",
        "Content-type": "application/json",
      },
      body: JSON.stringify(e),
    })
      .then(async (data) => {
        let resp = await data.json();
        if (
          selectedClient != null &&
          selectedClient?._id == resp?.data?.client_id
        ) {
          let selecteds: any = { ...selectedClient };
          selecteds.clients_products = selectedClient.clients_products.map(
            (item: any) => {
              if (item.productId == resp.data.productId) {
                return resp.data;
              } else {
                return item;
              }
            },
          );
          setSelectedClient(selecteds);
        }
      })
      .catch((_) => {
        // Erro silencioso
      });
  };

  const columns_filled: Column<FilledFormOutputDTO>[] = [
    {
      key: "name",
      header: "Formulário",
      render(_, item) {
        return item?.data ? getFirstFieldValue(item.data) : "N/A";
      },
    },
    {
      key: "status",
      header: "Status",
      render(_, item) {
        return (
          <div
            className={`rounded-full px-2 py-1 text-center text-xs font-semibold ${
              item.status === "OPEN"
                ? "bg-yellow-100 text-yellow-800"
                : "bg-green-100 text-green-800"
            }`}
          >
            {item.status
              ? StatusLabels[item.status as keyof typeof StatusLabels]
              : "N/A"}
          </div>
        );
      },
    },
    {
      key: "createdAt",
      header: "Data",
      render(_, item) {
        return item.createdAt
          ? new Date(item.createdAt).toLocaleDateString("pt-BR")
          : "N/A";
      },
    },
    {
      key: "actions",
      header: "Ações",
      render(_, item) {
        return (
          <IconButton
            onClick={() => {
              window.open(`/filled-forms/${item._id}`, "_blank");
            }}
          >
            <VisibilityIcon />
          </IconButton>
        );
      },
    },
  ];

  const columns_docs: Column<any>[] = [
    {
      key: "name",
      header: "Documento",
      render(_, item) {
        return item.name;
      },
    },
    {
      key: "actions",
      header: "Ações",
      render(_, item) {
        return (
          <IconButton
            onClick={() => {
              window.open(
                API_URL + "/processes/download/" + item.fileId,
                "_blank",
              );
            }}
          >
            <VisibilityIcon />
          </IconButton>
        );
      },
    },
  ];

  const handleClose = () => {
    setSelectedClient(null);
    setSelectType("formulario");
    setProductsIdSelect(null);
    setSelectedPhase(null);
    setSelectedActivity(null);
    setSelectTypeModal("fases");
    setActivitiesChecked({});
  };

  if (!selectedClient) return null;

  return (
    <Dialog
      maxWidth="xl"
      style={{ height: "100vh" }}
      fullWidth
      open={selectedClient != null}
      onClose={handleClose}
    >
      <DialogTitle>
        <h1 className="text-2xl font-bold mb-6">{selectedClient?.name}</h1>
        <p style={{ fontSize: 16 }}>
          <b>Celular:</b> {selectedClient?.whatsapp}
        </p>

        <div
          style={{
            display: "flex",
            marginTop: 15,
            marginBottom: 15,
            justifyContent: "center",
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <div
            onClick={() => setSelectType("formulario")}
            style={{
              width: 250,
              maxWidth: "50%",
              height: 40,
              borderRadius: 10,
              border: "1px solid #f2f2f2",
              cursor: "pointer",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              textAlign: "center",
              background: selectType == "formulario" ? "#1777CF" : "white",
              color: selectType == "formulario" ? "white" : "#1777CF",
            }}
          >
            Formulários Gerais
          </div>
          <div
            onClick={() => setSelectType("formulario_preenchidos")}
            style={{
              width: 250,
              maxWidth: "50%",
              height: 40,
              borderRadius: 10,
              border: "1px solid #f2f2f2",
              cursor: "pointer",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              textAlign: "center",
              background:
                selectType == "formulario_preenchidos" ? "#1777CF" : "white",
              color:
                selectType == "formulario_preenchidos" ? "white" : "#1777CF",
            }}
          >
            Formulários Preenchidos
          </div>
          <div
            onClick={() => setSelectType("produto")}
            style={{
              width: 250,
              maxWidth: "50%",
              height: 40,
              borderRadius: 10,
              border: "1px solid #f2f2f2",
              cursor: "pointer",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              textAlign: "center",
              background: selectType == "produto" ? "#1777CF" : "white",
              color: selectType == "produto" ? "white" : "#1777CF",
            }}
          >
            Produtos
          </div>
          <div
            onClick={() => setSelectType("documents")}
            style={{
              width: 250,
              maxWidth: "50%",
              height: 40,
              borderRadius: 10,
              border: "1px solid #f2f2f2",
              cursor: "pointer",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              textAlign: "center",
              background: selectType == "documents" ? "#1777CF" : "white",
              color: selectType == "documents" ? "white" : "#1777CF",
            }}
          >
            Documentos
          </div>
        </div>

        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <a target="_blank" hidden id="link_open" rel="noreferrer"></a>

        {selectType == "formulario" && (
          <div
            className="grid grid-cols-1"
            style={{ padding: 10, backgroundColor: "#f2f2f2", margin: 2 }}
          >
            <PhaseFormList
              hide_add
              clientId={selectedClient?._id}
              productId={productsIdSelect?._id}
            />
          </div>
        )}

        {selectType == "formulario_preenchidos" && (
          <div
            className="grid grid-cols-1"
            style={{ padding: 10, backgroundColor: "#f2f2f2", margin: 2 }}
          >
            <DataTable
              data={selectedClient?.filled_forms ?? []}
              columns={columns_filled}
              newFormat
              basePath="filled-forms"
              hide_add
              hide_actions={false}
              hideEdit={true}
              loading={loading}
              error={error}
              loadingComponent={
                <LoadingSkeleton title={false} filter={false} />
              }
              actions={[
                {
                  label: "Enviar para preencher documentos",
                  icon: <SendIcon />,
                  id: "send_doc",
                  onClick: async (item: FilledFormOutputDTO) => {
                    const phaseFormId =
                      item.phaseForm || (item as any).phaseFormId;
                    let link = `${window.location.origin}/forms/${phaseFormId}/${item.processId}/no-product/${selectedClient._id}`;
                    navigator.clipboard.writeText(link);
                  },
                },
                {
                  label: "Visualizar",
                  icon: <VisibilityIcon />,
                  onClick: async (item) => {
                    const linkOpen = document.getElementById(
                      "link_open",
                    ) as HTMLAnchorElement;
                    if (linkOpen) {
                      linkOpen.href =
                        window.location.origin +
                        `/filled-forms/view/${item._id}`;
                      linkOpen.click();
                    }
                  },
                },
              ]}
            />
          </div>
        )}

        {selectType == "produto" && (
          <div
            className="grid grid-cols-3"
            style={{
              padding: 10,
              backgroundColor: "#f2f2f2",
              margin: 2,
              height: "50vh",
            }}
          >
            <div
              className="scrollbar-hide"
              style={{
                backgroundColor: "white",
                borderRadius: 10,
                position: "relative",
                margin: 2,
                height: "100%",
                overflow: "scroll",
              }}
            >
              <div style={{ padding: 5 }}>
                <p
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginTop: 10,
                    zIndex: 2,
                  }}
                >
                  <img src="/31-1.svg" alt="icon" />
                  <b style={{ marginLeft: 10 }}>
                    Produtos (
                    {
                      products.filter((product) => productsIds[product._id])
                        .length
                    }
                    )
                  </b>
                </p>
              </div>

              {productsIds["no-product"] && (
                <div
                  style={{
                    textOverflow: "ellipsis",
                    padding: 5,
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    setProductsIdSelect({
                      _id: "no-product",
                      name: "Sem Produto",
                    } as any);
                    let activities_checked: Record<string, any> = {};
                    selectedClient?.clients_products?.map((item: any) => {
                      if (item.productId == "no-product") {
                        item.activities?.map((item2: any) => {
                          activities_checked[item2.activityId] = item2;
                        });
                      }
                    });
                    setActivitiesChecked(activities_checked);
                    setSelectTypeModal("fases");
                  }}
                >
                  <p
                    style={{
                      color:
                        productsIdSelect?._id == "no-product"
                          ? "#1777CF"
                          : "rgba(0, 0, 0, 0.87)",
                    }}
                  >
                    Sem Produto
                  </p>
                </div>
              )}

              {products.map(
                (product, index) =>
                  product._id !== "no-product" &&
                  productsIds[product._id] != undefined && (
                    <div
                      key={index}
                      style={{
                        textOverflow: "ellipsis",
                        padding: 5,
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        setProductsIdSelect(product);
                        let activities_checked: Record<string, any> = {};
                        selectedClient?.clients_products?.map((item: any) => {
                          if (item.productId == product._id) {
                            item.activities?.map((item2: any) => {
                              activities_checked[item2.activityId] = item2;
                            });
                          }
                        });
                        setActivitiesChecked(activities_checked);
                        setSelectTypeModal("fases");
                      }}
                    >
                      <p
                        style={{
                          color:
                            productsIdSelect?._id == product._id
                              ? "#1777CF"
                              : "rgba(0, 0, 0, 0.87)",
                        }}
                      >
                        {product.name}
                      </p>
                    </div>
                  ),
              )}
            </div>

            <div
              className="scrollbar-hide grid grid-cols-1 col-span-2"
              style={{
                backgroundColor: "white",
                borderRadius: 10,
                margin: 2,
                height: "100%",
                overflow: "scroll",
              }}
            >
              {productsIdSelect != null && (
                <div style={{ padding: 10 }}>
                  <div
                    style={{
                      display: "flex",
                      marginTop: 15,
                      marginBottom: 15,
                      justifyContent: "center",
                      alignItems: "center",
                      flexWrap: "wrap",
                    }}
                  >
                    <div
                      onClick={() => setSelectTypeModal("fases")}
                      style={{
                        width: 250,
                        maxWidth: "50%",
                        height: 40,
                        borderRadius: 10,
                        border: "1px solid #f2f2f2",
                        cursor: "pointer",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        textAlign: "center",
                        background:
                          selectTypeModal == "fases" ? "#1777CF" : "white",
                        color: selectTypeModal == "fases" ? "white" : "#1777CF",
                      }}
                    >
                      Fases
                    </div>
                    <div
                      onClick={() => setSelectTypeModal("atividade")}
                      style={{
                        width: 250,
                        maxWidth: "50%",
                        height: 40,
                        borderRadius: 10,
                        border: "1px solid #f2f2f2",
                        cursor: "pointer",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        textAlign: "center",
                        background:
                          selectTypeModal == "atividade" ? "#1777CF" : "white",
                        color:
                          selectTypeModal == "atividade" ? "white" : "#1777CF",
                      }}
                    >
                      Atividades
                    </div>
                  </div>

                  {selectTypeModal == "fases" && (
                    <div>
                      {productsIdSelect?.phases?.map(
                        (phase: any, index: number) => (
                          <div
                            key={index}
                            style={{ padding: 10, cursor: "pointer" }}
                            onClick={() => {
                              setSelectedPhase(phase._id);
                              setSelectTypeModal("atividade");
                            }}
                          >
                            <p
                              style={{
                                color:
                                  selectedPhase == phase._id
                                    ? "#1777CF"
                                    : "rgba(0, 0, 0, 0.87)",
                              }}
                            >
                              {phase.name}
                            </p>
                          </div>
                        ),
                      )}
                    </div>
                  )}

                  {selectTypeModal == "atividade" && (
                    <div style={{ marginTop: 15 }}>
                      {productsIdSelect?.phases
                        ?.filter((item: any) => item._id == selectedPhase)[0]
                        ?.activities?.map((activity: any, index: number) => (
                          <div
                            key={index}
                            style={{
                              padding: 10,
                              cursor: "pointer",
                              border: "1px solid #f2f2f2",
                              borderRadius: 10,
                              marginBottom: 5,
                            }}
                            onClick={() => {
                              setSelectedActivity(activity._id);
                              setSelectTypeModalDoc("documentos");
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                              }}
                            >
                              <p
                                style={{
                                  color:
                                    selectedActivity == activity._id
                                      ? "#1777CF"
                                      : "rgba(0, 0, 0, 0.87)",
                                }}
                              >
                                {activity.name}
                              </p>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                <p style={{ fontSize: 12, marginRight: 10 }}>
                                  {activitiesChecked[activity._id]?.status ||
                                    "Pendente"}
                                </p>
                                <div
                                  style={{
                                    width: 20,
                                    height: 20,
                                    borderRadius: 5,
                                    border: "1px solid #1777CF",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    background: activitiesChecked[activity._id]
                                      ? "#1777CF"
                                      : "white",
                                  }}
                                >
                                  {activitiesChecked[activity._id] && (
                                    <Check size={15} color="white" />
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              )}

              {selectedActivity != null && selectTypeModal == "atividade" && (
                <div style={{ padding: 10 }}>
                  <div
                    style={{
                      display: "flex",
                      marginTop: 15,
                      marginBottom: 15,
                      justifyContent: "center",
                      alignItems: "center",
                      flexWrap: "wrap",
                    }}
                  >
                    <div
                      onClick={() => setSelectTypeModalDoc("documentos")}
                      style={{
                        width: 250,
                        maxWidth: "50%",
                        height: 40,
                        borderRadius: 10,
                        border: "1px solid #f2f2f2",
                        cursor: "pointer",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        textAlign: "center",
                        background:
                          selectTypeModalDoc == "documentos"
                            ? "#1777CF"
                            : "white",
                        color:
                          selectTypeModalDoc == "documentos"
                            ? "white"
                            : "#1777CF",
                      }}
                    >
                      Documentos
                    </div>
                    <div
                      onClick={() => setSelectTypeModalDoc("formularios")}
                      style={{
                        width: 250,
                        maxWidth: "50%",
                        height: 40,
                        borderRadius: 10,
                        border: "1px solid #f2f2f2",
                        cursor: "pointer",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        textAlign: "center",
                        background:
                          selectTypeModalDoc == "formularios"
                            ? "#1777CF"
                            : "white",
                        color:
                          selectTypeModalDoc == "formularios"
                            ? "white"
                            : "#1777CF",
                      }}
                    >
                      Formulários
                    </div>
                  </div>

                  {selectTypeModalDoc == "documentos" && (
                    <div>
                      {productsIdSelect?.phases
                        ?.filter((item: any) => item._id == selectedPhase)[0]
                        ?.activities?.filter(
                          (item: any) => item._id == selectedActivity,
                        )[0]
                        ?.documents?.map((docId: string, index: number) => {
                          const doc = documents.find(
                            (d: any) => d._id === docId,
                          );
                          return (
                            <div
                              key={index}
                              style={{
                                padding: 10,
                                borderBottom: "1px solid #f2f2f2",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <p>{doc?.name || "Documento não encontrado"}</p>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    saveClientProduct({
                                      client_id: selectedClient._id,
                                      productId:
                                        productsIdSelect?._id || "no-product",
                                      activityId: selectedActivity,
                                      documentId: docId,
                                      status: "Concluído",
                                    });
                                  }}
                                >
                                  <Check
                                    size={18}
                                    color={
                                      activitiesChecked[
                                        selectedActivity
                                      ]?.documents?.includes(docId)
                                        ? "#1777CF"
                                        : "#ccc"
                                    }
                                  />
                                </IconButton>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  )}

                  {selectTypeModalDoc == "formularios" && (
                    <div style={{ marginTop: 15 }}>
                      {productsIdSelect?.phases
                        ?.filter((item: any) => item._id == selectedPhase)[0]
                        ?.activities?.filter(
                          (item: any) => item._id == selectedActivity,
                        )[0]
                        ?.phaseForms?.map((form: any, index: number) => (
                          <div
                            key={index}
                            style={{
                              padding: 10,
                              cursor: "pointer",
                              border: "1px solid #f2f2f2",
                              borderRadius: 10,
                              marginBottom: 5,
                            }}
                            onClick={() => {
                              const linkOpen = document.getElementById(
                                "link_open",
                              ) as HTMLAnchorElement;
                              if (linkOpen) {
                                if (
                                  selectedClient?.phaseFormsIdFilled &&
                                  selectedClient?.phaseFormsIdFilled[form._id]
                                ) {
                                  linkOpen.href =
                                    window.location.origin +
                                    `/filled-forms/view/${selectedClient?.phaseFormsIdFilled[form._id]._id}`;
                                } else {
                                  linkOpen.href =
                                    window.location.origin +
                                    `/forms/${form._id}/new/${productsIdSelect?._id || "no-product"}/${selectedClient._id}`;
                                }
                                linkOpen.click();
                              }
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                              }}
                            >
                              <p>{form.name}</p>
                              {selectedClient?.phaseFormsIdFilled &&
                                selectedClient?.phaseFormsIdFilled[
                                  form._id
                                ] && <Check size={18} color="#1777CF" />}
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {selectType == "documents" && (
          <div
            className="grid grid-cols-1"
            style={{ padding: 10, backgroundColor: "#f2f2f2", margin: 2 }}
          >
            <DataTable
              data={Object.values(selectedClient?.docsFilled ?? {})}
              columns={columns_docs}
              newFormat
              basePath="filled-forms"
              hide_add
              disable_scroll
              hide_actions={false}
              hideEdit={true}
              loading={loading}
              error={error}
              loadingComponent={
                <LoadingSkeleton title={false} filter={false} />
              }
              actions={[
                {
                  label: "Visualizar",
                  icon: <VisibilityIcon />,
                  onClick: async (item: any) => {
                    const linkOpen = document.getElementById(
                      "link_open",
                    ) as HTMLAnchorElement;
                    if (linkOpen) {
                      linkOpen.href = item.path;
                      linkOpen.click();
                    }
                  },
                },
              ]}
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ClientViewModal;
